import { useRef, useState, type PointerEvent } from "react";
import { clsx } from "clsx";
import {
  AppStoreBadge,
  GooglePlayBadge,
} from "@/assets/images/offer/volt-zero";
import styles from "./LandingReviews.module.scss";

export interface LandingReview {
  name: string;
  // ISO date, shown as "26 ian. 2026".
  date: string;
  quote: string;
  avatar?: string;
  // The store the review was left in, badged on the avatar.
  store?: "google-play" | "app-store";
}

interface LandingReviewsProps {
  reviews: LandingReview[];
}

// How far a mouse drag goes before it moves to the next card.
const DRAG_THRESHOLD_PX = 40;

const formatReviewDate = (isoDate: string) =>
  new Intl.DateTimeFormat("ro-RO", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
    .format(new Date(isoDate))
    .toLowerCase();

function ReviewCard({ review }: { review: LandingReview }) {
  const StoreBadge =
    review.store === "google-play" ? GooglePlayBadge : AppStoreBadge;

  return (
    <article className={styles.review}>
      <div className={styles.reviewHeader}>
        <div className={styles.reviewAuthor}>
          {review.avatar && (
            <div className={styles.reviewAvatar}>
              <img
                src={review.avatar}
                alt=""
                className={styles.reviewAvatarImage}
              />
              {review.store && (
                <StoreBadge className={styles.reviewStore} aria-hidden />
              )}
            </div>
          )}
          <div className={styles.reviewNameBlock}>
            <p className={styles.reviewName}>{review.name}</p>
            <p className={styles.reviewDate}>{formatReviewDate(review.date)}</p>
          </div>
        </div>
        <span className={styles.reviewStars} aria-label="5 stele">
          ★★★★★
        </span>
      </div>
      <p className={styles.reviewQuote}>{review.quote}</p>
    </article>
  );
}

// Customer reviews, one card at a time, swiped or picked with the dots.
export function LandingReviews({ reviews }: LandingReviewsProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startScroll: number } | null>(
    null,
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const cardStep = () => {
    const track = trackRef.current;
    const card = track?.firstElementChild as HTMLElement | null;
    return track && card
      ? card.offsetWidth + parseFloat(getComputedStyle(track).columnGap)
      : 0;
  };

  const handleScroll = () => {
    const step = cardStep();
    if (step && trackRef.current) {
      setActiveIndex(Math.round(trackRef.current.scrollLeft / step));
    }
  };

  const scrollTo = (index: number) => {
    trackRef.current?.scrollTo({
      left: index * cardStep(),
      behavior: "smooth",
    });
  };

  // Touch and trackpads scroll the track natively; a mouse can only drag it.
  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (event.pointerType !== "mouse" || event.button !== 0 || !track) {
      return;
    }

    dragRef.current = { startX: event.clientX, startScroll: track.scrollLeft };
    track.setPointerCapture(event.pointerId);
    setIsDragging(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (drag && trackRef.current) {
      trackRef.current.scrollLeft =
        drag.startScroll - (event.clientX - drag.startX);
    }
  };

  // A drag past the threshold moves one card in its direction, like a swipe.
  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const step = cardStep();
    if (!drag) {
      return;
    }

    dragRef.current = null;
    setIsDragging(false);
    if (!step) {
      return;
    }

    const distance = drag.startX - event.clientX;
    const direction =
      Math.abs(distance) < DRAG_THRESHOLD_PX ? 0 : Math.sign(distance);
    const index = Math.round(drag.startScroll / step) + direction;
    scrollTo(Math.min(Math.max(index, 0), reviews.length - 1));
  };

  return (
    <div className={styles.root}>
      <div
        ref={trackRef}
        className={clsx(styles.track, isDragging && styles["track--dragging"])}
        onScroll={handleScroll}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {reviews.map((review) => (
          <ReviewCard key={review.name} review={review} />
        ))}
      </div>
      <div className={styles.dots}>
        {reviews.map((review, index) => (
          <button
            key={review.name}
            type="button"
            className={clsx(
              styles.dot,
              index === activeIndex && styles["dot--active"],
            )}
            aria-label={`Recenzia ${index + 1}`}
            aria-current={index === activeIndex}
            onClick={() => scrollTo(index)}
          />
        ))}
      </div>
    </div>
  );
}
